/**
 * Flavour family selector (Phase 1) — multi-select order form.
 * - Tiles toggle selection in place; each selected tile has its own quantity stepper.
 * - The main product form's add-to-cart button becomes a bulk add for all selected
 *   lines (one /cart/add.js request), mirroring product-form.js for section rendering,
 *   drawer update, cart icon, events and error handling.
 * - Without JS the native form still adds the current product; interception only
 *   happens when this component is live.
 * Guarded so the script can be included more than once (product template + wrapper section).
 */
if (!customElements.get('flavour-family-selector')) {
  class FlavourFamilySelector extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      // Ordered selection state: variantId (string) -> { qty, price, tile }
      this.items = new Map();

      // Formats (1 kg pose / 12 × 50 g breve): each has its own tile grid and
      // tier strip (.js-flavour-format-panel[data-format]); the selection and
      // the bulk add span both. Without a toggle everything is format 'kg'.
      this.formatBtns = Array.from(this.querySelectorAll('.js-flavour-format'));
      this.formatPanels = Array.from(this.querySelectorAll('.js-flavour-format-panel'));
      this.activeFormat = this.dataset.format || 'kg';

      // Volume tiers per format, { kg: [[minQty, percent], ...] } ascending —
      // display only; the numbers must match the admin automatic discounts.
      // Only full-price tiles (data-volume-eligible) count toward a tier or
      // receive the percentage, mirroring checkout where the discount's
      // automated collection excludes items on sale. Tiers never mix across
      // formats (each format's discount is scoped to its own collection).
      this.tiersByFormat = {};
      try {
        let parsed = JSON.parse(this.dataset.discountTiers || '{}');
        if (Array.isArray(parsed)) parsed = { kg: parsed };
        Object.keys(parsed).forEach((format) => {
          this.tiersByFormat[format] = (Array.isArray(parsed[format]) ? parsed[format] : [])
            .filter((tier) => Array.isArray(tier) && tier[0] > 0 && tier[1] > 0)
            .sort((a, b) => a[0] - b[0]);
        });
      } catch (error) {
        this.tiersByFormat = {};
      }
      this.tierEls = Array.from(this.querySelectorAll('.js-flavour-tier'));
      this.savingEl = this.querySelector('.js-flavour-saving');
      this.stickySavingEl = this.querySelector('.js-flavour-sticky-saving');
      this.progressEl = this.querySelector('.js-flavour-progress');

      this.errorMsg = this.querySelector('.js-flavour-error');
      this.sectionEl = this.closest('.shopify-section');
      this.form = this.sectionEl ? this.sectionEl.querySelector('.js-product-form') : null;
      this.submitBtn = this.form ? this.form.querySelector('[name="add"]') : null;
      this.submitBtnLocked = !!(this.submitBtn && this.submitBtn.disabled);
      this.mainQtyInput = this.form ? this.form.querySelector('[name="quantity"]') : null;
      this.gallery = this.sectionEl ? this.sectionEl.querySelector('media-gallery') : null;
      // Parent page: every flavour's image is appended to the gallery up front
      // (per format) and a selected tile jumps to its slide. Child pages keep
      // the select-to-append behaviour.
      this.galleryAll = this.dataset.galleryAll === 'true';

      // Parent mode: the parent product is unbuyable by design (stock 0), so the
      // server renders the button disabled ("Udsolgt") — that lock must not stick;
      // the CTA is ours to control (disabled until a flavour is selected).
      this.parentMode = this.dataset.parentMode === 'true';
      if (this.parentMode) {
        this.submitBtnLocked = false;

        // The parent's own price (0 kr) is CSS-hidden by the snippet; take it
        // over: "Fra <cheapest>" at rest, the selection total once picking.
        // Both states use formatMoney so the element never mixes formats.
        this.priceEl = this.sectionEl
          ? this.sectionEl.querySelector('.product-price .price__current')
          : null;
        if (this.priceEl) {
          this.priceFromText = (this.dataset.priceFromLabel || '[price]')
            .replace('[price]', this.formatMoney(parseInt(this.dataset.priceFrom, 10) || 0));
          const priceWrap = this.priceEl.closest('.price');
          if (priceWrap) priceWrap.style.visibility = 'visible';
        }
      }

      const idInput = this.form ? this.form.querySelector('[name="id"]') : null;
      this.currentTile = (idInput && this.querySelector(`.flavour-tile[data-variant-id="${idInput.value}"]`))
        || this.querySelector('.flavour-tile--selected');

      // Seed state from server-rendered selection (current product at qty 1).
      this.querySelectorAll('.flavour-tile--selected').forEach((tile) => {
        this.setTileState(tile, true, this.getTileQty(tile));
      });

      // Mobile sticky ATC bar: without a usable product form (no form at all,
      // or a genuinely sold-out product keeping the button locked) it could
      // never add anything, so it comes out of the DOM entirely.
      this.stickyBar = this.querySelector('.js-flavour-sticky-bar');
      if (this.stickyBar) {
        if (!this.form || this.submitBtnLocked) {
          this.stickyBar.remove();
          this.stickyBar = null;
          this.stickySavingEl = null;
        } else {
          this.initStickyBar();
        }
      }

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', this.handleChange.bind(this));
      if (this.form) this.form.addEventListener('submit', this.handleSubmit.bind(this));
      if (this.mainQtyInput) {
        this.sectionEl.addEventListener('change', this.handleSectionChange.bind(this));
      }

      this.updateCta();
      if (this.galleryAll) {
        try {
          this.initMedia();
        } catch (error) {
          // Degrade silently — the gallery is an enhancement, not a dependency.
        }
      }
    }

    tileFormat(tile) {
      return tile.dataset.format || 'kg';
    }

    tiersFor(format) {
      return this.tiersByFormat[format] || [];
    }

    /**
     * Switches the visible format (tile grid + tier strip), keeping the
     * selection made in the other format — the CTA adds both.
     * @param {string} format - Format key (kg / breve).
     */
    setFormat(format) {
      if (!format || format === this.activeFormat) return;
      this.activeFormat = format;
      this.dataset.format = format;
      this.formatBtns.forEach((btn) => {
        btn.setAttribute('aria-pressed', btn.dataset.format === format ? 'true' : 'false');
      });
      this.formatPanels.forEach((panel) => {
        panel.hidden = panel.dataset.format !== format;
      });
      this.updateCta();
      if (this.galleryAll) {
        try {
          this.syncMedia(true);
        } catch (error) {
          // Gallery is best-effort.
        }
      }
    }

    getTileQty(tile) {
      const input = tile.querySelector('.js-flavour-qty');
      const qty = input ? parseInt(input.value, 10) : 1;
      return Number.isNaN(qty) || qty < 1 ? 1 : qty;
    }

    /**
     * Handles clicks on tile toggles and stepper buttons.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const formatBtn = evt.target.closest('.js-flavour-format');
      if (formatBtn) {
        evt.preventDefault();
        this.setFormat(formatBtn.dataset.format);
        return;
      }

      const toggle = evt.target.closest('.js-flavour-toggle');
      if (toggle && !toggle.disabled) {
        evt.preventDefault();
        const tile = toggle.closest('.flavour-tile');
        if (this.items.has(tile.dataset.variantId)) {
          this.setTileState(tile, false);
        } else {
          this.setTileState(tile, true, 1);
        }
        return;
      }

      const stepBtn = evt.target.closest('.qty-input__btn');
      if (stepBtn) {
        evt.preventDefault();
        const tile = stepBtn.closest('.flavour-tile');
        const input = tile.querySelector('.js-flavour-qty');
        if (stepBtn.name === 'plus') {
          input.stepUp();
        } else {
          input.stepDown();
        }
        this.applyQtyInput(tile);
      }
    }

    /**
     * Handles direct edits of a tile's quantity input.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      if (!evt.target.matches('.js-flavour-qty')) return;
      this.applyQtyInput(evt.target.closest('.flavour-tile'));
    }

    applyQtyInput(tile) {
      const input = tile.querySelector('.js-flavour-qty');
      let qty = parseInt(input.value, 10);
      if (Number.isNaN(qty)) qty = 1;

      if (qty <= 0) {
        this.setTileState(tile, false);
      } else {
        this.setTileState(tile, true, qty);
      }
    }

    /**
     * Selects/deselects a tile and records its quantity.
     * @param {object} tile - The .flavour-tile element.
     * @param {boolean} selected - New selection state.
     * @param {number} [qty] - Quantity when selecting.
     */
    setTileState(tile, selected, qty) {
      const variantId = tile.dataset.variantId;
      const toggle = tile.querySelector('.js-flavour-toggle');
      const input = tile.querySelector('.js-flavour-qty');

      const wasSelected = this.items.has(variantId);

      if (selected) {
        this.items.set(variantId, {
          qty,
          price: parseInt(tile.dataset.price, 10) || 0,
          eligible: tile.dataset.volumeEligible !== 'false',
          format: this.tileFormat(tile),
          tile
        });
        tile.classList.add('flavour-tile--selected');
        if (toggle) {
          toggle.setAttribute('aria-pressed', 'true');
          toggle.setAttribute('aria-label', toggle.dataset.labelDeselect);
        }
        if (input && input.value !== String(qty)) input.value = qty;
        if (tile === this.currentTile) this.syncMainQtyInput(qty);
      } else {
        this.items.delete(variantId);
        tile.classList.remove('flavour-tile--selected');
        if (toggle) {
          toggle.setAttribute('aria-pressed', 'false');
          toggle.setAttribute('aria-label', toggle.dataset.labelSelect);
        }
        if (input) input.value = 1;
      }

      // Mirror the selection in the media gallery (never for the current
      // product — its image is already the product's own media). Gallery
      // integration is best-effort: it must never break the order form.
      if (tile !== this.currentTile && selected !== wasSelected) {
        try {
          if (this.galleryAll) {
            if (selected) this.showMediaFor(variantId);
          } else if (selected) {
            this.appendGallerySlide(tile);
          } else {
            this.removeGallerySlide(tile);
          }
        } catch (error) {
          // Degrade silently — the gallery is an enhancement, not a dependency.
        }
      }

      this.updateCta();
    }

    /**
     * Pushes the current product tile's quantity to the main form's quantity input.
     * @param {number} qty - Quantity to set.
     */
    syncMainQtyInput(qty) {
      if (!this.mainQtyInput) return;
      if (this.mainQtyInput.value !== String(qty)) {
        this.mainQtyInput.value = qty;
        this.mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /**
     * Reflects changes made to the main quantity input (e.g. via the form's own stepper).
     * @param {object} evt - Event object.
     */
    handleSectionChange(evt) {
      if (evt.target !== this.mainQtyInput) return;
      if (!this.currentTile || !this.items.has(this.currentTile.dataset.variantId)) return;

      const qty = parseInt(this.mainQtyInput.value, 10);
      if (!Number.isNaN(qty) && qty > 0) {
        this.setTileState(this.currentTile, true, qty);
      }
    }

    /**
     * Wires up the mobile sticky ATC bar (always visible on mobile): route
     * taps to the main CTA — or scroll back to the tiles while nothing is
     * selected — and pad the page bottom so the bar never permanently covers
     * the last content.
     */
    initStickyBar() {
      this.stickyBtn = this.stickyBar.querySelector('.js-flavour-sticky-btn');
      this.stickyPrice = this.stickyBar.querySelector('.js-flavour-sticky-price');

      if (this.stickyBtn) {
        this.stickyBtn.addEventListener('click', () => {
          if (this.items.size === 0) {
            const grid = this.querySelector('.flavour-family-selector__grid:not([hidden])');
            if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else {
            // Same code path as tapping the form's own button (submit event,
            // bulk-add interception, loading states).
            this.submitBtn.click();
          }
        });
      }

      this.updateStickyBarPadding();
      // The bar is display:none from the md breakpoint up (offsetHeight 0).
      window.addEventListener('on:breakpoint-change', () => this.updateStickyBarPadding());
    }

    /**
     * Pads the page bottom by the bar's current height (0 on desktop, taller
     * on the parent while the from-price hint shows).
     */
    updateStickyBarPadding() {
      if (!this.stickyBar) return;
      const height = this.stickyBar.offsetHeight;
      document.body.style.paddingBottom = height ? `${height}px` : '';
    }

    formatMoney(cents) {
      return (cents / 100).toLocaleString(Shopify.locale, {
        style: 'currency',
        currency: Shopify.currency.active
      });
    }

    // Whole-krone display for the saving badge, matching the tier chips'
    // rounding (client request). Totals and checkout keep the exact øre.
    formatMoneyRounded(cents) {
      return Math.round(cents / 100).toLocaleString(Shopify.locale, {
        style: 'currency',
        currency: Shopify.currency.active,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    }

    /**
     * Updates the main add-to-cart button label ("Læg i kurv (N) – TOTAL") and
     * disables it while nothing is selected. The total is the discounted total
     * once the full-price quantity reaches a volume tier; checkout applies the
     * matching automatic discount, so the two must never disagree.
     */
    updateCta() {
      let count = 0;
      let total = 0;
      const eligibleByFormat = {};
      this.items.forEach((item) => {
        count += item.qty;
        total += item.qty * item.price;
        if (item.eligible) {
          eligibleByFormat[item.format] = (eligibleByFormat[item.format] || 0) + item.qty;
        }
      });

      // Per format, the highest tier its full-price quantity reaches (tiers
      // sorted ascending). The percentage is applied per line with per-line
      // rounding, matching how Shopify allocates a percentage discount across
      // cart lines.
      const activeTiers = {};
      Object.keys(eligibleByFormat).forEach((format) => {
        this.tiersFor(format).forEach((tier) => {
          if (eligibleByFormat[format] >= tier[0]) activeTiers[format] = tier;
        });
      });
      let discount = 0;
      this.items.forEach((item) => {
        const tier = activeTiers[item.format];
        if (tier && item.eligible) {
          discount += Math.round((item.qty * item.price * tier[1]) / 100);
        }
      });
      const discountedTotal = total - discount;

      this.updateTierUi(activeTiers, discount, eligibleByFormat);

      // Parent mode: the main price mirrors the CTA total ("Fra <cheapest>"
      // while nothing is selected).
      if (this.parentMode && this.priceEl) {
        this.priceEl.textContent = count > 0 ? this.formatMoney(discountedTotal) : this.priceFromText;
      }

      if (!this.submitBtn || this.submitBtnLocked) return;

      const label = count === 0
        ? this.dataset.ctaEmptyLabel
        : this.dataset.ctaTemplate
          .replace('[count]', count)
          .replace('[total]', this.formatMoney(discountedTotal));

      this.submitBtn.textContent = label;
      this.submitBtn.disabled = count === 0;

      // The sticky bar's button carries the same label but stays tappable when
      // empty ("Vælg smag" scrolls to the tiles); on the parent the from-price
      // hint shows only while the label has no total in it.
      if (this.stickyBtn) {
        this.stickyBtn.textContent = label;
        if (this.stickyPrice) {
          this.stickyPrice.hidden = count > 0;
          if (count === 0) this.stickyPrice.textContent = this.priceFromText || '';
        }
        // The hint and the savings line change the bar's height, so keep the
        // page padding in step.
        this.updateStickyBarPadding();
      }
    }

    /**
     * Highlights the reached tier chip, shows/hides the savings line (inline
     * above the CTA, and inside the mobile sticky bar) and the progress nudge
     * toward the next tier. The nudge's wording carries the full-price rule
     * ("Mix 1 fuldprisvare mere og spar 10%"), so a customer whose sale items
     * aren't moving the count gets the explanation right where they look.
     * The chips highlight per format; the nudge follows the visible format.
     * @param {object} activeTiers - Applied [minQty, percent] tier per format.
     * @param {number} discount - Discount in cents, all formats.
     * @param {object} eligibleByFormat - Full-price units selected per format.
     */
    updateTierUi(activeTiers, discount, eligibleByFormat) {
      this.tierEls.forEach((el) => {
        const activeTier = activeTiers[el.dataset.format || 'kg'];
        const isActive = !!activeTier && parseInt(el.dataset.qty, 10) === activeTier[0];
        el.classList.toggle('flavour-tier--active', isActive);
      });

      const text = discount > 0
        ? (this.dataset.savingTemplate || '[amount]').replace('[amount]', this.formatMoneyRounded(discount))
        : '';
      [this.savingEl, this.stickySavingEl].forEach((el) => {
        if (!el) return;
        el.hidden = !text;
        el.textContent = text;
      });

      if (!this.progressEl) return;
      const eligibleCount = eligibleByFormat[this.activeFormat] || 0;
      const nextTier = this.tiersFor(this.activeFormat).find((tier) => eligibleCount < tier[0]);
      let progress = '';
      if (nextTier) {
        if (eligibleCount === 0) {
          progress = (this.dataset.progressStart || '')
            .replace('[qty]', nextTier[0]);
        } else {
          const remaining = nextTier[0] - eligibleCount;
          progress = ((remaining === 1 ? this.dataset.progressOne : this.dataset.progressOther) || '')
            .replace('[count]', remaining);
        }
        progress = progress.replace('[percent]', nextTier[1]);
      }
      this.progressEl.hidden = !progress;
      this.progressEl.textContent = progress;
    }

    /**
     * Parent page: moves the server-rendered flavour slides (.js-flavour-media
     * template, one per flavour per format) into the theme's media gallery,
     * after the parent's own media, and shows the active format's. Flavours
     * sharing one image (the breve boxes) share one slide. The gallery script
     * is deferred ahead of this one, so it has already initialised.
     */
    initMedia() {
      const tpl = this.sectionEl ? this.sectionEl.querySelector('.js-flavour-media') : null;
      const gallery = this.gallery;
      if (!tpl || !gallery || typeof gallery.setVisibleItems !== 'function') return;

      const viewer = gallery.querySelector('.media-viewer');
      const thumbs = gallery.querySelector('.media-thumbs');
      if (!viewer) { tpl.remove(); return; }

      // The template holds a slide + thumbnail pair per flavour, in order.
      // Pair them by position (not media id: duplicated products share the
      // same media id, so the breve boxes all carry one id). Flavours whose
      // image URL repeats within a format collapse into the first slide.
      const seen = {};
      const slides = Array.from(tpl.content.querySelectorAll('.media-viewer__item'));
      const thumbItems = Array.from(tpl.content.querySelectorAll('.media-thumbs__item'));
      slides.forEach((item, index) => {
        const thumb = thumbItems[index];
        const img = item.querySelector('img');
        const key = `${item.dataset.heroFormat}|${img ? (img.dataset.src || img.getAttribute('src')) : index}`;
        if (seen[key]) {
          seen[key].dataset.heroVariantIds += ` ${item.dataset.heroVariantId}`;
          return;
        }
        item.dataset.heroVariantIds = item.dataset.heroVariantId;
        seen[key] = item;
        FlavourFamilySelector.loadImages(item);
        viewer.appendChild(item);
        if (thumb && thumbs) {
          FlavourFamilySelector.loadImages(thumb);
          thumbs.appendChild(thumb);
        }
      });
      tpl.remove();

      gallery.zoomLinks = gallery.querySelectorAll('.js-zoom-link');
      gallery.zoomLinks.forEach((el) => {
        el.addEventListener('click', (evt) => evt.preventDefault());
      });
      if (gallery.loadingSpinner && typeof gallery.initZoom === 'function') gallery.initZoom();

      this.syncMedia();
    }

    /**
     * Shows only the active format's flavour slides (the parent's own media
     * always stays) and re-syncs the gallery's slider state.
     * @param {boolean} [jump] - Move to the format's first own slide (breve →
     *   the box) so a format switch visibly changes the picture; back on 1 kg
     *   the parent's own first image is the format's picture.
     */
    syncMedia(jump) {
      const gallery = this.gallery;
      if (!gallery || typeof gallery.setVisibleItems !== 'function') return;

      gallery.querySelectorAll('[data-hero-format]').forEach((el) => {
        el.style.display = el.dataset.heroFormat === this.activeFormat ? '' : 'none';
      });
      gallery.thumbsItems = gallery.querySelectorAll('.media-thumbs__item');
      gallery.setVisibleItems();

      // A parent with a single image never got its slider listeners.
      if (gallery.viewerItemOffset === undefined && typeof gallery.initGallery === 'function') {
        gallery.initGallery();
      }
      if (gallery.visibleItems.length > 1) {
        gallery.viewerItemOffset = gallery.visibleItems[1].offsetLeft - gallery.visibleItems[0].offsetLeft;
      }
      gallery.visibleItems.forEach((item) => FlavourFamilySelector.loadImages(item));

      const bulletsEl = gallery.querySelector('.media-ctrl__bullets');
      if (bulletsEl) {
        bulletsEl.innerHTML = '';
        gallery.visibleItems.forEach((_item, index) => {
          const bullet = document.createElement('span');
          bullet.className = 'media-ctrl__bullet tw-w-4 tw-h-4 tw-rounded-full tw-transition-colors tw-duration-300 tw-bg-gray-300';
          bullet.dataset.index = index;
          bulletsEl.appendChild(bullet);
        });
      }

      let keep = gallery.visibleItems.includes(gallery.currentItem)
        ? gallery.currentItem
        : gallery.visibleItems[0];
      if (jump) {
        const own = gallery.visibleItems.find((item) => item.dataset.heroFormat === this.activeFormat);
        keep = this.activeFormat === 'kg' ? gallery.visibleItems[0] : (own || gallery.visibleItems[0]);
      }
      gallery.currentItem = null;
      if (keep && typeof gallery.customSetActiveMedia === 'function') gallery.customSetActiveMedia(keep, true);
    }

    /**
     * Loads a slide's images now. The theme's lazy-image pass (main.js
     * initLazyImages, run on a timeout after this script) only handles
     * [loading="lazy"] images that exist at that moment, so slides appended
     * here, and any image this script flips to eager, get their data-src /
     * data-srcset copied over explicitly.
     * @param {Element} item - Slide or thumbnail element.
     */
    static loadImages(item) {
      item.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        const nodes = img.parentNode.tagName === 'PICTURE' ? Array.from(img.parentNode.children) : [img];
        nodes.forEach((el) => {
          if (el.dataset.src && !el.getAttribute('src')) el.src = el.dataset.src;
          if (el.dataset.srcset && !el.getAttribute('srcset')) el.srcset = el.dataset.srcset;
        });
        img.loading = 'eager';
      });
    }

    /**
     * Jumps the gallery to the selected flavour's slide (parent page).
     * @param {string} variantId - The tile's variant id.
     */
    showMediaFor(variantId) {
      const gallery = this.gallery;
      if (!gallery || !variantId || !gallery.visibleItems) return;
      const item = Array.from(gallery.querySelectorAll(`.media-viewer__item[data-hero-format="${this.activeFormat}"]`))
        .find((el) => (el.dataset.heroVariantIds || '').split(' ').includes(String(variantId)));
      if (item && gallery.visibleItems.includes(item) && typeof gallery.customSetActiveMedia === 'function') {
        gallery.customSetActiveMedia(item, true);
      }
    }

    /**
     * Appends the tile's flavour image to the media gallery as a new slide
     * (plus thumbnail and nav bullet where the gallery shows them) and scrolls
     * to it. The slide structure/classes are cloned from the gallery's own
     * server-rendered slides so sizing and ratio always match the section
     * settings; appended slides deliberately skip the hover-zoom wiring.
     * @param {object} tile - The selected .flavour-tile element.
     */
    appendGallerySlide(tile) {
      const gallery = this.gallery;
      const src = tile.dataset.galleryImage;
      if (!gallery || !src) return;

      const viewer = gallery.querySelector('.media-viewer');
      const refItem = viewer && viewer.querySelector('.media-viewer__item');
      if (!refItem) return;

      const mediaId = `flavour-${tile.dataset.variantId}`;
      if (viewer.querySelector(`[data-media-id="${mediaId}"]`)) return;

      const li = document.createElement('li');
      li.className = 'media-viewer__item flavour-gallery-slide';
      li.dataset.mediaId = mediaId;
      li.dataset.mediaType = 'image';

      const refMedia = refItem.querySelector('.media');
      const media = document.createElement('div');
      media.className = refMedia ? refMedia.className : 'media relative';
      if (refMedia && refMedia.style.paddingTop) media.style.paddingTop = refMedia.style.paddingTop;

      const cover = document.createElement('div');
      cover.className = 'media--cover overflow-hidden absolute top-0 left-0 w-full h-full';

      const img = document.createElement('img');
      const refImg = refItem.querySelector('.product-image');
      img.className = refImg ? refImg.className : 'product-image img-fit img-fit--contain w-full';
      img.src = src;
      img.alt = tile.dataset.flavourName || '';

      cover.appendChild(img);
      media.appendChild(cover);
      li.appendChild(media);
      viewer.appendChild(li);

      const thumbs = gallery.querySelector('.media-thumbs');
      if (thumbs) {
        const refThumbBtn = thumbs.querySelector('.media-thumbs__btn');
        const thumbLi = document.createElement('li');
        thumbLi.className = 'media-thumbs__item flavour-gallery-thumb';
        thumbLi.dataset.mediaId = mediaId;

        const thumbBtn = document.createElement('button');
        thumbBtn.type = 'button';
        thumbBtn.className = 'media-thumbs__btn media relative w-full';
        thumbBtn.setAttribute('aria-controls', 'gallery-viewer');
        if (refThumbBtn && refThumbBtn.style.paddingTop) {
          thumbBtn.style.paddingTop = refThumbBtn.style.paddingTop;
        }

        const thumbImg = document.createElement('img');
        const refThumbImg = thumbs.querySelector('.media-thumbs__btn img');
        thumbImg.className = refThumbImg ? refThumbImg.className : 'img-fit w-full';
        thumbImg.src = tile.dataset.galleryThumb || src;
        thumbImg.alt = img.alt;

        thumbBtn.appendChild(thumbImg);
        thumbLi.appendChild(thumbBtn);
        thumbs.appendChild(thumbLi);
      }

      const bullets = gallery.querySelector('.media-ctrl__bullets');
      if (bullets) {
        const refBullet = bullets.querySelector('.media-ctrl__bullet');
        const bullet = document.createElement('span');
        bullet.className = refBullet
          ? `${refBullet.className.replace('tw-bg-black', 'tw-bg-gray-300')} flavour-gallery-bullet`
          : 'media-ctrl__bullet flavour-gallery-bullet';
        bullets.appendChild(bullet);
      }

      if (typeof gallery.setVisibleItems === 'function') gallery.setVisibleItems();
      // A single-image gallery bails out of initGallery() on load (no slider
      // needed); the first appended slide makes it a real slider, so init now.
      if (!gallery.viewerItemOffset && typeof gallery.initGallery === 'function') {
        gallery.initGallery();
      }
      if (typeof gallery.customSetActiveMedia === 'function') {
        gallery.customSetActiveMedia(li, true);
      }
    }

    /**
     * Removes a previously appended gallery slide (with its thumbnail/bullet)
     * and restores a sensible active slide without stealing focus.
     * @param {object} tile - The deselected .flavour-tile element.
     */
    removeGallerySlide(tile) {
      const gallery = this.gallery;
      if (!gallery) return;

      const mediaId = `flavour-${tile.dataset.variantId}`;
      const slide = gallery.querySelector(`.media-viewer__item[data-media-id="${mediaId}"]`);
      if (!slide) return;

      const wasCurrent = gallery.currentItem === slide;
      const prevCurrent = gallery.currentItem;
      let fallback = slide.previousElementSibling || slide.nextElementSibling;

      const thumb = gallery.querySelector(`.media-thumbs__item[data-media-id="${mediaId}"]`);
      const bullet = gallery.querySelector('.flavour-gallery-bullet');
      slide.remove();
      if (thumb) thumb.remove();
      if (bullet) bullet.remove();

      if (typeof gallery.setVisibleItems === 'function') gallery.setVisibleItems();
      if (typeof gallery.customSetActiveMedia !== 'function'
        || !gallery.visibleItems || gallery.visibleItems.length === 0) return;

      let target = wasCurrent ? fallback : prevCurrent;
      if (!target || gallery.visibleItems.indexOf(target) === -1) {
        [target] = gallery.visibleItems;
      }
      // Indices shifted; null the cached item so the gallery re-syncs its
      // counter, arrows, bullets and thumbs even when the target is unchanged.
      gallery.currentItem = null;
      gallery.customSetActiveMedia(target, true);
    }

    /**
     * Bulk-adds all selected lines in one request. Runs on the form itself
     * (event target), so it fires before product-form.js's ancestor listener;
     * stopPropagation keeps that single-item handler out of the way.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (this.items.size === 0) return;

      evt.preventDefault();
      evt.stopPropagation();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.setErrorMsgState();
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');
      if (this.stickyBtn) this.stickyBtn.classList.add('is-loading');

      const requestedItems = [];
      this.items.forEach((item, variantId) => {
        requestedItems.push({ id: parseInt(variantId, 10), quantity: item.qty });
      });

      const cartDrawer = document.querySelector('cart-drawer');
      let sections = 'cart-icon-bubble';
      if (cartDrawer) {
        sections += `,${cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: requestedItems,
            sections,
            sections_url: window.location.pathname
          })
        });
        const data = await response.json();
        const error = typeof data.description === 'string' ? data.description : data.message;

        if (data.status) this.setErrorMsgState(error);
        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page' || theme.settings.afterAtc === 'no-js') {
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
          return;
        }

        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble && data.sections) {
          cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
        }

        setTimeout(() => {
          if (cartDrawer) {
            cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
          } else if (window.location.pathname === theme.routes.cart) {
            const cartItems = document.querySelector('cart-items');
            if (cartItems) {
              if (cartItems.dataset.empty === 'true') {
                window.location.reload();
              } else {
                cartItems.refresh();
              }
            }
          }
        }, 700);

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();

        requestedItems.forEach((requested) => {
          const itemInOldCart = oldCartData.items.filter(
            (item) => item.variant_id === requested.id
          )[0];
          const itemInNewCart = newCartData.items.filter(
            (item) => item.variant_id === requested.id
          )[0];

          if (itemInOldCart) {
            this.dispatchEvent(new CustomEvent('on:line-item:change', {
              bubbles: true,
              detail: {
                cart: newCartData,
                variantId: requested.id,
                oldQuantity: itemInOldCart.quantity,
                newQuantity: itemInNewCart ? itemInNewCart.quantity : itemInOldCart.quantity
              }
            }));
          } else {
            this.dispatchEvent(new CustomEvent('on:cart:add', {
              bubbles: true,
              detail: {
                cart: newCartData,
                variantId: requested.id
              }
            }));
          }
        });

        this.resetSelection();
      } catch (error) {
        console.log(error); // eslint-disable-line
        if (!this.errorMsg || this.errorMsg.hidden) {
          this.setErrorMsgState(this.dataset.errorMessage);
        }
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg ? this.errorMsg.textContent : this.dataset.errorMessage
          }
        }));
      } finally {
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        if (this.stickyBtn) this.stickyBtn.classList.add('is-success');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
          if (this.stickyBtn) {
            this.stickyBtn.classList.remove('is-loading');
            this.stickyBtn.classList.remove('is-success');
          }
        }, 1400);
      }
    }

    /**
     * After a successful bulk add: back to just the current product at qty 1.
     */
    resetSelection() {
      Array.from(this.items.values()).forEach((item) => this.setTileState(item.tile, false));
      if (this.currentTile) {
        this.setTileState(this.currentTile, true, 1);
      }
    }

    /**
     * Shows/hides the error message. Mirrors product-form.js setErrorMsgState.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      if (!this.errorMsg) return;
      this.errorMsg.hidden = !error;
      if (error) this.errorMsg.textContent = error;
    }
  }

  customElements.define('flavour-family-selector', FlavourFamilySelector);
}
